import { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import * as Analytics from '../services/AnalyticsService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VideoContext = createContext(null);
export const useVideo = () => useContext(VideoContext);

export const VideoProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [isActive, setIsActive] = useState(false);
  const [mode, setMode] = useState('mini');
  const [videoUrl, setVideoUrl] = useState(null);
  const [gameId, setGameId] = useState(null);
  const [timecodes, setTimecodes] = useState([]);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [embeddedRect, setEmbeddedRect] = useState(null);
  const [miniSize, setMiniSize] = useState({ width: 320, height: 180 });
  const [miniHidden, setMiniHidden] = useState(true); // Hidden by default on panorama
  const [countdown, setCountdown] = useState(0); // Countdown timer for auto-resume
  const countdownIntervalRef = useRef(null);
  const [showFlash, setShowFlash] = useState(false); // Flash effect on timecode navigation
  const [videoQuality, setVideoQuality] = useState(null); // Current video quality level
  const dragStartRef = useRef({ x: 0, y: 0 });
  const savedTimeRef = useRef(0);
  const mouseDownTimeRef = useRef(0);
  const mouseDownPosRef = useRef({ x: 0, y: 0 });
  const isDraggingRef = useRef(false);
  const resizeStartRef = useRef({ width: 320, height: 180, x: 0, y: 0 });
  
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const lastNavTimeRef = useRef(-1);
  const restoredRef = useRef(false);

  const isOnPanoramaPage = location.pathname.includes('/guess/');
  const [isMobile, setIsMobile] = useState(false);
  
  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // RESTORE on panorama page
  useEffect(() => {
    if (restoredRef.current) return;
    if (!isOnPanoramaPage) return;
    
    try {
      const saved = sessionStorage.getItem('videoPlayerState');
      if (!saved) return;
      
      const state = JSON.parse(saved);
      if (!state || !state.isActive || !state.videoUrl) return;
      
      restoredRef.current = true;
      setGameId(state.gameId);
      setVideoUrl(state.videoUrl);
      setTimecodes(state.timecodes || []);
      lastNavTimeRef.current = state.lastNavTime || -1;
      savedTimeRef.current = state.currentTime || 0;
      setMode('mini');
      setEmbeddedRect(null);
      setShowControls(true);
      setMiniHidden(false);
    setIsActive(true);
    Analytics.trackVideoStart(gId, "game_page", "embedded");
    } catch (e) {}
  }, [isOnPanoramaPage]);

  // Clear state on game main page
  useEffect(() => {
    if (!isOnPanoramaPage && !location.pathname.includes('/guess/')) {
      sessionStorage.removeItem('videoPlayerState');
    }
  }, [location.pathname, isOnPanoramaPage]);

  const loadTimecodes = useCallback(async (gId) => {
    try {
      const res = await axios.get(`${API}/games/${gId}/video/timecodes`);
      setTimecodes(res.data || []);
    } catch { setTimecodes([]); }
  }, []);

  const startVideoEmbedded = useCallback(async (gId, url, rect) => {
    sessionStorage.removeItem('videoPlayerState');
    if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
    setGameId(gId);
    setVideoUrl(url);
    setMode('embedded');
    setEmbeddedRect(rect);
    setCurrentTime(0);
    setDuration(0);
    savedTimeRef.current = 0;
    lastNavTimeRef.current = -1;
    setMiniHidden(false);
    setIsActive(true);
    Analytics.trackVideoStart(gId, "game_page", "embedded");
    await loadTimecodes(gId);
  }, [loadTimecodes]);

  const startVideo = useCallback(async (gId, url) => {
    sessionStorage.removeItem('videoPlayerState');
    if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
    setGameId(gId);
    setVideoUrl(url);
    setMode('mini');
    setEmbeddedRect(null);
    setPos({ x: 0, y: 0 });
    setCurrentTime(0);
    setDuration(0);
    savedTimeRef.current = 0;
    lastNavTimeRef.current = -1;
    setMiniHidden(false);
    setIsActive(true);
    Analytics.trackVideoStart(gId, "game_page", "embedded");
    await loadTimecodes(gId);
  }, [loadTimecodes]);

  const startVideoAtTime = useCallback(async (gId, url, time) => {
    sessionStorage.removeItem('videoPlayerState');
    if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
    setGameId(gId);
    setVideoUrl(url);
    setMode('mini');
    setEmbeddedRect(null);
    setPos({ x: 0, y: 0 });
    savedTimeRef.current = time;
    lastNavTimeRef.current = -1;
    setMiniHidden(false);
    setIsActive(true);
    Analytics.trackVideoStart(gId, "game_page", "embedded");
    await loadTimecodes(gId);
  }, [loadTimecodes]);

  const stopVideo = useCallback(() => {
    Analytics.trackVideoEnd(duration, false, "closed");
    if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
    if (videoRef.current) { videoRef.current.src = ''; }
    setIsActive(false);
    setVideoUrl(null);
    setGameId(null);
    setTimecodes([]);
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);
    setMode('mini');
    setEmbeddedRect(null);
    sessionStorage.removeItem('videoPlayerState');
  }, []);

  // Initialize video
  useEffect(() => {
    if (!isActive || !videoUrl) return;
    
    const initVideo = () => {
      const v = videoRef.current;
      if (!v) { setTimeout(initVideo, 100); return; }
      
      if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
      
      v.onloadedmetadata = () => {
        if (v.videoWidth && v.videoHeight) {
          setVideoQuality({ height: v.videoHeight, width: v.videoWidth, bitrate: 0 });
        }
      };
      v.oncanplay = () => {
        // Set video quality from native resolution if not set by HLS
        if (v.videoWidth && v.videoHeight) {
          setVideoQuality({ height: v.videoHeight, width: v.videoWidth, bitrate: 0 });
        }
        setDuration(v.duration || 0);
        if (savedTimeRef.current > 0) {
          v.currentTime = savedTimeRef.current;
          savedTimeRef.current = 0;
        }
        v.play().catch((e) => console.error("Play error:", e));
      };
      v.ondurationchange = () => setDuration(v.duration || 0);
      v.ontimeupdate = () => setCurrentTime(v.currentTime);
      v.onplay = () => setIsPlaying(true);
      v.onpause = () => setIsPlaying(false);
      
      const isHls = videoUrl.includes('.m3u8');
      
      if (!isHls) { v.src = videoUrl; v.load(); return; }
      
      // Check for native HLS support (Safari/iOS)
      const nativeHlsSupport = v.canPlayType('application/vnd.apple.mpegurl') || 
                               v.canPlayType('application/x-mpegURL');
      
      if (nativeHlsSupport) {
        // Use native HLS support (Safari, iOS)
        console.log("Using native HLS support");
        v.src = videoUrl;
        v.load();
        
        v.onloadedmetadata = () => {
          if (savedTimeRef.current > 0) {
            v.currentTime = savedTimeRef.current;
            savedTimeRef.current = 0;
          }
          v.play().catch(e => console.error("Native HLS play error:", e));
        };
        return;
      }
      
      if (window.Hls?.isSupported()) {
        const hls = new window.Hls({
          // Adaptive bitrate streaming based on screen size and bandwidth
          capLevelToPlayerSize: true,
          maxBufferLength: 30,
          maxMaxBufferLength: 60,
          startLevel: -1, // Auto-select quality based on bandwidth
          abrEwmaDefaultEstimate: 500000, // Initial bandwidth estimate (500kbps)
          abrBandWidthFactor: 0.95,
          abrBandWidthUpFactor: 0.7,
          autoStartLoad: true,
          debug: false
        });
        hls.loadSource(videoUrl);
        hls.attachMedia(v);
        
        // Listen for quality level changes
        hls.on(window.Hls.Events.LEVEL_SWITCHED, (event, data) => {
          const level = hls.levels[data.level];
          if (level) {
            setVideoQuality({ height: level.height, width: level.width, bitrate: level.bitrate });
          }
        });
        
        hls.on(window.Hls.Events.MANIFEST_PARSED, () => {
          // Auto-start playback
          if (savedTimeRef.current > 0) {
            v.currentTime = savedTimeRef.current;
            savedTimeRef.current = 0;
          }
          v.play().catch(e => console.error("HLS play error:", e));
          const currentLevel = hls.currentLevel >= 0 ? hls.levels[hls.currentLevel] : hls.levels[0];
          if (currentLevel) {
            setVideoQuality({ height: currentLevel.height, width: currentLevel.width, bitrate: currentLevel.bitrate });
          }
        });
        
        hlsRef.current = hls;
        console.log("HLS initialized, levels:", hls.levels.length);
      }
    };
    
    setTimeout(initVideo, 100);
  }, [isActive, videoUrl]);

  // Timecode navigation with flash effect and countdown
  useEffect(() => {
    if (!isActive || !timecodes.length || !gameId) return;
    
    const checkTimecodes = () => {
      const v = videoRef.current;
      if (!v) return;
      
      // Don't check if countdown is active
      if (countdown > 0) return;
      
      // Only check when video is playing
      if (v.paused) return;
      
      const currentTime = v.currentTime;
      
      // Find the next timecode that we just crossed
      for (const tc of timecodes) {
        const timecodeTime = tc.timecode;
        // Check if we just crossed this timecode (within last 0.3s)
        if (currentTime >= timecodeTime && currentTime < timecodeTime + 0.3) {
          // Check if we haven't navigated to this one yet
          if (lastNavTimeRef.current !== timecodeTime) {
            lastNavTimeRef.current = timecodeTime;
            const targetPath = '/games/' + gameId + '/guess/' + tc.panorama_id;
            
            if (location.pathname !== targetPath) {
              console.log('Timecode triggered:', timecodeTime, 'navigating to:', targetPath);
              
              // Track timecode event
              Analytics.trackTimecodeTriggered(gameId, tc.panorama_id, timecodeTime, v.currentTime, 'auto');
              
              // 1. Pause video
              v.pause();
              
              // 2. Show flash effect
              setShowFlash(true);
              
              // 3. Play shutter sound
              try {
                const audio = new Audio('/game/sounds/shutter.wav');
                audio.volume = 0.4;
                audio.play().catch(e => console.log('Audio play failed:', e));
              } catch(e) {
                console.log('Audio error:', e);
              }
              
              // 4. Store state for countdown after navigation
              sessionStorage.setItem('videoPlayerState', JSON.stringify({
                isActive: true,
                videoUrl,
                gameId,
                timecodes,
                currentTime: v.currentTime,
                lastNavTime: timecodeTime,
                needsCountdown: true
              }));
              
              // 5. Navigate after flash (150ms)
              setTimeout(() => {
                setShowFlash(false);
                navigate(targetPath);
              }, 150);
              
              return; // Exit after handling
            }
          }
        }
      }
    };
    
    const interval = setInterval(checkTimecodes, 100);
    return () => clearInterval(interval);
  }, [isActive, timecodes, gameId, location.pathname, navigate, videoUrl, countdown]);
  
  // Handle countdown start after navigation to panorama
  useEffect(() => {
    if (!isOnPanoramaPage) return;
    
    const savedState = sessionStorage.getItem('videoPlayerState');
    if (!savedState) return;
    
    try {
      const state = JSON.parse(savedState);
      if (state.needsCountdown) {
        console.log('Starting countdown after navigation');
        
        // Clear the flag
        state.needsCountdown = false;
        sessionStorage.setItem('videoPlayerState', JSON.stringify(state));
        
        // Show mini player and start countdown
        setMiniHidden(false);
        setCountdown(5);
      }
    } catch(e) {
      console.log('Error parsing state:', e);
    }
  }, [isOnPanoramaPage, location.pathname]);

  // Countdown timer effect for auto-resume
  useEffect(() => {
    if (countdown > 0) {
      countdownIntervalRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            // Countdown finished - resume playback
            clearInterval(countdownIntervalRef.current);
            const v = videoRef.current;
            if (v) v.play().catch(() => {});
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      return () => {
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
        }
      };
    }
  }, [countdown]);

  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    // If countdown is active, cancel it and resume
    if (countdown > 0) {
      setCountdown(0);
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    }
    if (v) v.paused ? v.play().catch(() => {}) : v.pause();
  }, [countdown]);
  
  const seek = useCallback((t) => { 
    if (videoRef.current) videoRef.current.currentTime = t;
  }, []);
  
  const fmt = (s) => !s || isNaN(s) ? '0:00' : `${Math.floor(s/60)}:${String(Math.floor(s%60)).padStart(2,'0')}`;

  // Mini player click/drag handling
  const DRAG_THRESHOLD = 5;
  const LONG_PRESS_TIME = 200;

  const onMouseDown = (e) => {
    if (mode !== 'mini') return;
    if (e.target.closest('button') || e.target.closest('.progress-bar') || e.target.closest('.controls') || e.target.closest('.resize-handle')) return;
    
    mouseDownTimeRef.current = Date.now();
    mouseDownPosRef.current = { x: e.clientX, y: e.clientY };
    dragStartRef.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
    isDraggingRef.current = false;
  };

  const onMouseMove = (e) => {
     // Handled by document event listeners
    
    if (mode !== 'mini' || mouseDownTimeRef.current === 0) return;
    
    const dx = Math.abs(e.clientX - mouseDownPosRef.current.x);
    const dy = Math.abs(e.clientY - mouseDownPosRef.current.y);
    const elapsed = Date.now() - mouseDownTimeRef.current;
    
    if (dx > DRAG_THRESHOLD || dy > DRAG_THRESHOLD || elapsed > LONG_PRESS_TIME) {
      isDraggingRef.current = true;
      setDragging(true);
      setPos({
        x: e.clientX - dragStartRef.current.x,
        y: e.clientY - dragStartRef.current.y
      });
    }
  };

  const onMouseUp = (e) => {
    // Reset drag state
    const wasDragging = isDraggingRef.current;
    mouseDownTimeRef.current = 0;
    isDraggingRef.current = false;
    setDragging(false);
    
    // Toggle play on click (not drag) in any mode
    if (!wasDragging && !e.target.closest("button") && !e.target.closest(".progress-bar") && !e.target.closest(".controls")) {
      togglePlay();
    }
  };

  const onMouseLeave = () => {
    if (mode !== 'mini') return;
    if (!resizing) {
      mouseDownTimeRef.current = 0;
      isDraggingRef.current = false;
      setDragging(false);
    }
  };

  // Touch handlers for mobile drag
  const playerRef = useRef(null);
  const posRef = useRef(pos);
  posRef.current = pos;
  
  const handleTouchStart = useCallback((e) => {
    if (mode !== 'mini') return;
    if (e.target.closest('button') || e.target.closest('.progress-bar') || e.target.closest('.controls')) return;
    
    const touch = e.touches[0];
    mouseDownTimeRef.current = Date.now();
    mouseDownPosRef.current = { x: touch.clientX, y: touch.clientY };
    dragStartRef.current = { x: touch.clientX - posRef.current.x, y: touch.clientY - posRef.current.y };
    isDraggingRef.current = false;
  }, [mode]);

  const handleTouchMove = useCallback((e) => {
    if (mode !== 'mini' || mouseDownTimeRef.current === 0) return;
    
    const touch = e.touches[0];
    const dx = Math.abs(touch.clientX - mouseDownPosRef.current.x);
    const dy = Math.abs(touch.clientY - mouseDownPosRef.current.y);
    const elapsed = Date.now() - mouseDownTimeRef.current;
    
    // Start dragging after threshold or long press
    if (dx > DRAG_THRESHOLD || dy > DRAG_THRESHOLD || elapsed > LONG_PRESS_TIME) {
      if (!isDraggingRef.current) {
        isDraggingRef.current = true;
        setDragging(true);
      }
      
      // Calculate new position with bounds checking
      let newX = touch.clientX - dragStartRef.current.x;
      let newY = touch.clientY - dragStartRef.current.y;
      
      // Keep within viewport
      const playerWidth = window.innerWidth <= 768 ? Math.min(260, window.innerWidth - 20) : miniSize.width;
      const playerHeight = window.innerWidth <= 768 ? Math.round(playerWidth * 9 / 16) : miniSize.height;
      const maxX = window.innerWidth - playerWidth - 10;
      const maxY = window.innerHeight - playerHeight - 10;
      newX = Math.max(-playerWidth + 50, Math.min(maxX, newX));
      newY = Math.max(-250, Math.min(maxY, newY));
      
      setPos({ x: newX, y: newY });
      e.preventDefault(); // Prevent scrolling while dragging
    }
  }, [mode, miniSize.width, miniSize.height]);

  const handleTouchEnd = useCallback((e) => {
    const wasDragging = isDraggingRef.current;
    const elapsed = Date.now() - mouseDownTimeRef.current;
    
    mouseDownTimeRef.current = 0;
    isDraggingRef.current = false;
    setDragging(false);
    
    // Toggle play on tap (not drag) - must be a quick tap
    if (!wasDragging && elapsed < 300) {
      const target = e.target;
      if (!target.closest('button') && !target.closest('.progress-bar') && !target.closest('.controls')) {
        e.preventDefault();
        togglePlay();
      }
    }
  }, [togglePlay]);

  // Attach touch listeners with passive: false to allow preventDefault
  useEffect(() => {
    const player = playerRef.current;
    if (!player || mode !== 'mini') return;
    
    player.addEventListener('touchstart', handleTouchStart, { passive: true });
    player.addEventListener('touchmove', handleTouchMove, { passive: false });
    player.addEventListener('touchend', handleTouchEnd, { passive: false });
    
    return () => {
      player.removeEventListener('touchstart', handleTouchStart);
      player.removeEventListener('touchmove', handleTouchMove);
      player.removeEventListener('touchend', handleTouchEnd);
    };
  }, [mode, handleTouchStart, handleTouchMove, handleTouchEnd]);


  const setFullscreen = (val) => {
    setMode(val ? "fullscreen" : "mini");
    
    // Track fullscreen toggle
    if (val) Analytics.trackFullscreenEnter(gameId, currentTime);
    else Analytics.trackFullscreenExit(gameId, currentTime);
    
    // Force highest quality for fullscreen, auto for mini
    console.log("setFullscreen called, hlsRef:", !!hlsRef.current, "levels:", hlsRef.current?.levels?.length);
    if (hlsRef.current && hlsRef.current.levels) {
      const levels = hlsRef.current.levels;
      if (levels.length > 0) {
        if (val) {
          // Fullscreen: set to highest quality (last level)
          const highestLevel = levels.length - 1;
          hlsRef.current.currentLevel = highestLevel;
          hlsRef.current.nextLevel = highestLevel;
          console.log("Fullscreen: forcing quality to", levels[highestLevel]?.height + "p");
          const lvl = levels[highestLevel];
          if (lvl) setVideoQuality({ height: lvl.height, width: lvl.width, bitrate: lvl.bitrate });
        } else {
          // Mini: return to auto
          hlsRef.current.currentLevel = -1;
          console.log("Mini: auto quality");
        }
      }
    }
  };

  const value = { 
    isActive, mode, startVideo, startVideoEmbedded, startVideoAtTime, stopVideo,
    togglePlay, isPlaying, isFullscreen: mode === 'fullscreen', setIsFullscreen: setFullscreen,
    seek, timecodes, videoRef, currentTime, duration, showControls, setShowControls, fmt,
    miniHidden, setMiniHidden, countdown, setCountdown, showFlash
  };

  const shouldRenderMini = isActive && mode === 'mini' && isOnPanoramaPage && !miniHidden;
  const shouldRenderEmbedded = isActive && mode === 'embedded' && embeddedRect;
  const shouldRenderFullscreen = isActive && mode === 'fullscreen';
  const shouldRender = shouldRenderMini || shouldRenderEmbedded || shouldRenderFullscreen;

  const getPlayerStyle = () => {
    if (mode === 'fullscreen') return { position: 'fixed', inset: 0, background: '#000', zIndex: 2147483647 };
    if (mode === 'mini') {
      // On mobile, position below the objects list
      const mobileCheck = window.innerWidth <= 768;
      const topPosition = mobileCheck ? 250 : 70;
      const rightPosition = mobileCheck ? 10 : 20;
      const playerWidth = mobileCheck ? Math.min(260, window.innerWidth - 20) : miniSize.width;
      const playerHeight = mobileCheck ? Math.round(playerWidth * 9 / 16) : miniSize.height;
      
      return {
        position: 'fixed', 
        top: topPosition, 
        right: rightPosition, 
        width: playerWidth, 
        height: playerHeight,
        background: '#000', 
        zIndex: 999999, 
        borderRadius: 8, 
        overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(0,0,0,0.4)', 
        cursor: dragging ? 'grabbing' : 'pointer',
        transform: `translate(${pos.x}px, ${pos.y}px)`,
        userSelect: 'none',
        touchAction: 'none'
      };
    }
    if (mode === 'embedded' && embeddedRect) return {
      position: 'fixed', top: embeddedRect.top, left: embeddedRect.left,
      width: embeddedRect.width, height: embeddedRect.height,
      background: '#000', zIndex: 2147483647, borderRadius: 12, overflow: 'hidden'
    };
    return { display: 'none' };
  };

  const isLarge = mode === 'embedded' || mode === 'fullscreen';

  return (
    <VideoContext.Provider value={value}>
      {children}
      {shouldRender && (
        <div 
          onMouseMove={(e) => { setShowControls(true); onMouseMove(e); }}
          onMouseLeave={onMouseLeave}
          onMouseDown={onMouseDown}
          onMouseUp={onMouseUp}
          ref={playerRef}
          style={getPlayerStyle()}
        >
          <video ref={videoRef} playsInline style={{ width: '100%', height: '100%', objectFit: mode === 'mini' ? 'cover' : 'contain', pointerEvents: 'none' }} />
          
          {/* Countdown overlay for mini player */}
          {mode === 'mini' && countdown > 0 && (
            <div
              onClick={(e) => {
                e.stopPropagation();
                Analytics.trackCountdownClick(gameId, currentTime, 5 - countdown);
                setCountdown(0);
                if (countdownIntervalRef.current) {
                  clearInterval(countdownIntervalRef.current);
                }
                const v = videoRef.current;
                if (v) v.play().catch(() => {});
              }}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0,0,0,0.75)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                zIndex: 10
              }}
            >
              {/* Circular countdown with number inside */}
              <div style={{
                position: 'relative',
                width: 80,
                height: 80,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {/* Background and progress circles */}
                <svg
                  style={{
                    position: 'absolute',
                    width: 80,
                    height: 80,
                    transform: 'rotate(-90deg)'
                  }}
                >
                  <circle
                    cx="40"
                    cy="40"
                    r="36"
                    fill="none"
                    stroke="rgba(255,255,255,0.2)"
                    strokeWidth="6"
                  />
                  <circle
                    cx="40"
                    cy="40"
                    r="36"
                    fill="none"
                    stroke="#f59e0b"
                    strokeWidth="6"
                    strokeLinecap="round"
                    strokeDasharray={2 * Math.PI * 36}
                    strokeDashoffset={2 * Math.PI * 36 * (1 - countdown / 5)}
                    style={{ transition: 'stroke-dashoffset 1s linear' }}
                  />
                </svg>
                {/* Countdown number */}
                <span style={{
                  fontSize: 32,
                  fontWeight: 700,
                  color: '#fff',
                  textShadow: '0 2px 8px rgba(0,0,0,0.5)',
                  zIndex: 1
                }}>
                  {countdown}
                </span>
              </div>
              {/* Play hint below */}
              <div style={{
                marginTop: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                color: 'rgba(255,255,255,0.9)',
                fontSize: 13,
                fontWeight: 500
              }}>
                <svg width={16} height={16} viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z"/>
                </svg>
                Нажмите для воспроизведения
              </div>
            </div>
          )}

          {/* Close button for mini player - top right corner */}
          {mode === 'mini' && (
            <button
              onClick={(e) => { 
                e.stopPropagation(); 
                setMiniHidden(true);
                // Stop countdown if active
                if (countdown > 0) {
                  setCountdown(0);
                  if (countdownIntervalRef.current) {
                    clearInterval(countdownIntervalRef.current);
                  }
                }
              }}
              style={{
                position: 'absolute',
                top: 8,
                right: 8,
                background: 'rgba(0,0,0,0.6)',
                border: 'none',
                borderRadius: '50%',
                width: 28,
                height: 28,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                zIndex: 20
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
          )}
          
          {/* Close button for fullscreen - top right corner */}
          {mode === 'fullscreen' && (
            <button
              onClick={(e) => { e.stopPropagation(); stopVideo(); }}
              style={{
                position: 'absolute',
                top: 20,
                right: 20,
                width: 40,
                height: 40,
                background: 'rgba(0,0,0,0.6)',
                border: 'none',
                borderRadius: '50%',
                color: '#fff',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 100,
                transition: 'background 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.8)'}
              onMouseOut={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.6)'}
            >
              <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
          )}
          {mode === 'mini' && (
            <button
              onClick={(e) => { e.stopPropagation(); if (videoRef.current) savedTimeRef.current = videoRef.current.currentTime; setMiniHidden(true); }}
              style={{
                position: 'absolute',
                top: 8,
                right: 8,
                width: 28,
                height: 28,
                background: 'rgba(0,0,0,0.6)',
                border: 'none',
                borderRadius: '50%',
                color: '#fff',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
                transition: 'background 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.8)'}
              onMouseOut={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.6)'}
            >
              <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
          )}
          
          )}
          {/* Fullscreen button and quality badge for mini player - bottom right */}
          {mode === 'mini' && (
            <div style={{
              position: 'absolute',
              bottom: 8,
              right: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              zIndex: 15
            }}>
              {videoQuality && (
                <span 
                  onClick={(e) => {
                    e.stopPropagation();
                    if (hlsRef.current && hlsRef.current.levels && hlsRef.current.levels.length > 1) {
                      const levels = hlsRef.current.levels;
                      const current = hlsRef.current.currentLevel;
                      const next = (current + 1) % levels.length;
                      hlsRef.current.currentLevel = next;
                      const lvl = levels[next];
                      if (lvl) setVideoQuality({ height: lvl.height, width: lvl.width, bitrate: lvl.bitrate });
                    }
                  }}
                  style={{
                    background: 'rgba(0,0,0,0.6)',
                    color: '#fff',
                    fontSize: 10,
                    padding: '3px 6px',
                    borderRadius: 4,
                    fontWeight: 500,
                    cursor: 'pointer'
                  }}>
                  {videoQuality.height}p
                </span>
              )}
              <button
                onClick={(e) => { e.stopPropagation(); setFullscreen(true); }}
                style={{
                  background: 'rgba(0,0,0,0.6)',
                  border: 'none',
                  borderRadius: 4,
                  color: '#fff',
                  cursor: 'pointer',
                  padding: 4,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Полноэкранный режим"
              >
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>
                </svg>
              </button>
            </div>
          )}
          
          <div className="controls" style={{
            position: 'absolute', left: 0, right: 0, bottom: 0,
            background: 'linear-gradient(transparent, rgba(0,0,0,.9))',
            padding: isLarge ? '20px' : '15px 12px',
            opacity: showControls ? 1 : 0, transition: 'opacity .2s',
            pointerEvents: showControls ? 'auto' : 'none'
          }}>
            <div className="progress-bar"
              onClick={(e) => { e.stopPropagation(); const r = e.currentTarget.getBoundingClientRect(); seek((e.clientX - r.left) / r.width * duration); }}
              style={{ height: isLarge ? 6 : 4, background: 'rgba(255,255,255,.3)', borderRadius: 4, marginBottom: isLarge ? 12 : 8, cursor: 'pointer', position: 'relative' }}>
              <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${duration ? currentTime / duration * 100 : 0}%`, background: '#fff', borderRadius: 4, pointerEvents: 'none' }} />
              {timecodes.map((t,i) => (
                <div key={i} onClick={(e) => { e.stopPropagation(); seek(t.timecode); }}
                  style={{ position: 'absolute', left: `${duration ? t.timecode / duration * 100 : 0}%`, top: '50%', transform: 'translate(-50%,-50%)',
                    width: isLarge ? 10 : 6, height: isLarge ? 10 : 6, background: '#f59e0b', borderRadius: '50%', cursor: 'pointer', zIndex: 1 }} />
              ))}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: isLarge ? 16 : 10 }}>
              <button onClick={(e) => { e.stopPropagation(); togglePlay(); }} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: 0 }}>
                {isPlaying ? <svg width={isLarge?24:16} height={isLarge?24:16} viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
                  : <svg width={isLarge?24:16} height={isLarge?24:16} viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>}
              </button>
              <span style={{ color: '#fff', fontSize: isLarge ? 13 : 10, flex: 1 }}>{fmt(currentTime)} / {fmt(duration)}</span>
              {mode !== 'mini' && videoQuality && (
                <span style={{
                  color: '#fff',
                  fontSize: isLarge ? 12 : 10,
                  background: 'rgba(255,255,255,0.2)',
                  padding: '2px 6px',
                  borderRadius: 4,
                  fontWeight: 500
                }}>
                  {videoQuality.height}p
                </span>
              )}
              {mode !== 'mini' && (
                <button onClick={(e) => { e.stopPropagation(); setFullscreen(mode !== 'fullscreen'); }} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: 0 }}>
                  <svg width={isLarge?20:14} height={isLarge?20:14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    {mode === 'fullscreen' ? <path d="M8 3v3a2 2 0 01-2 2H3m18 0h-3a2 2 0 01-2-2V3m0 18v-3a2 2 0 012-2h3M3 16h3a2 2 0 012 2v3"/> : <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>}
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </VideoContext.Provider>
  );
};

export default VideoContext;
